import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { StateModel } from 'redux/reducers';
import { useSelector } from 'react-redux';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { AssociationEntries } from 'enums/onboarding/crm';
import { IDocumentAssociationInfo } from 'typings/documents/documents';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { generateFieldPlaceholderForAdditionalField } from '../utils';

// components
import FieldIssue from '../FieldIssue';
import SubmitButtons from '../SubmitButtons';
import AdditionalField from 'components/Forms/FormComponents/AdditionalField';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import { Col, Divider, FieldValidationRule, FormField, Row, Text } from '@ui';

interface IProps {
  formId: string;
}

const InnerForm = ({ formId }: IProps) => {
  const { t } = useTranslation('onboarding');
  const { values } = useFormikContext<FormValuesModel>();

  const currentContactId = useSelector<StateModel>(
    (state) => state.auth.contactData?._id,
  );

  const documentAssociation = useMemo<
    IDocumentAssociationInfo | undefined
  >(() => {
    return {
      id: currentContactId as string,
      type: AssociationEntries.Contact,
    };
  }, [currentContactId]);

  return (
    <>
      <Row wrap={true} justify="start">
        {values.additionalFields.map((ad, i) => (
          <Col key={i} {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField
              label={
                <>
                  <FieldIssue
                    fieldId={`supplementaryQuestions.${formId}["${ad.id}"].valueJSON`}
                  >
                    {ad.name}
                  </FieldIssue>
                  {values.additionalFields[i].description ? (
                    <StyledText>
                      {values.additionalFields[i].description}
                    </StyledText>
                  ) : null}
                </>
              }
              name={`additionalFields.${i}.value`}
              component={AdditionalField}
              additionalProps={{
                renderPlaceholder: (validationRules: FieldValidationRule[]) =>
                  generateFieldPlaceholderForAdditionalField(
                    validationRules || [],
                    ad,
                    t,
                  ),
                field: {
                  type: ad.type,
                  options: ad.options.selectOptions,
                  formDocument: ad.options.formDocument,
                  documentAssociation,
                },
              }}
            />
          </Col>
        ))}
      </Row>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colorAdditionalL1};
`;

export default InnerForm;
